import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, Clapperboard, Hand, Loader2, Music, Sparkles, X } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@meno/ui/components/ui/dialog'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import { NavItem, NavRail } from '@/components/DialogNavRail'
import PlaylistContentPicker from '@/components/PlaylistContentPicker'
import TransitionPickerDialog from '@/components/TransitionPickerDialog'
import { TransitionPreview } from '@/components/TransitionPreview'
import { TRANSITIONS } from '@/lib/transitions'
import { useMixCatalogs } from '@/lib/mixCatalogs'

// MixDialog — "New Mix" used to fire a blind POST (name = "New mix") and
// jump straight to the detail page with no confirmation step, the only
// "New" action in Content Hub that skipped a real creation dialog. Now it's
// the same left-nav-rail creation modal Playlist/Layout use, adapted to a
// Mix's nature: instead of one "Content" + one "Companions" tab, a Mix has
// four independent single-item slots — Content (playlist or layout), Audio,
// Stop Screen, Transition — each gets its own tab. Content/Audio/Stop Screen
// embed PlaylistContentPicker in its single-select "custom groups" mode
// (same component the picker-driven tabs use elsewhere); Transition reuses
// the same visual preview picker CompanionSection uses.
type NavKey = 'content' | 'audio' | 'stopScreen' | 'transition'

export type MixDialogSave = {
  name: string
  playlistId: number | null
  layoutId: number | null
  audioId: number | null
  audioPlaylistId: number | null
  stopScreenId: number | null
  transition: string | null
}

export type MixDialogProps = {
  onSave: (data: MixDialogSave) => Promise<void>
  onClose: () => void
}

export default function MixDialog({ onSave, onClose }: MixDialogProps) {
  const { t } = useTranslation()
  const { contentGroups, audioGroups, stopScreenGroups } = useMixCatalogs()

  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [activeNav, setActiveNav] = useState<NavKey>('content')

  const [playlistId, setPlaylistId] = useState<number | null>(null)
  const [layoutId, setLayoutId] = useState<number | null>(null)
  const [audioId, setAudioId] = useState<number | null>(null)
  const [audioPlaylistId, setAudioPlaylistId] = useState<number | null>(null)
  const [stopScreenId, setStopScreenId] = useState<number | null>(null)
  const [transition, setTransition] = useState<string | null>(null)
  const [transitionPickerOpen, setTransitionPickerOpen] = useState(false)

  const contentValue = playlistId != null ? `playlist:${playlistId}` : layoutId != null ? `layout:${layoutId}` : null
  const setContentValue = (v: string | null) => {
    if (v?.startsWith('playlist:')) { setPlaylistId(Number(v.slice(9))); setLayoutId(null) }
    else if (v?.startsWith('layout:')) { setPlaylistId(null); setLayoutId(Number(v.slice(7))) }
    else { setPlaylistId(null); setLayoutId(null) }
  }
  const audioValue = audioId != null ? `file:${audioId}` : audioPlaylistId != null ? `playlist:${audioPlaylistId}` : null
  const setAudioValue = (v: string | null) => {
    if (v?.startsWith('file:')) { setAudioId(Number(v.slice(5))); setAudioPlaylistId(null) }
    else if (v?.startsWith('playlist:')) { setAudioId(null); setAudioPlaylistId(Number(v.slice(9))) }
    else { setAudioId(null); setAudioPlaylistId(null) }
  }
  const stopScreenValue = stopScreenId != null ? `stopScreen:${stopScreenId}` : null
  const setStopScreenValue = (v: string | null) => setStopScreenId(v != null ? Number(v.slice(11)) : null)

  const transitionLabel = transition ? (TRANSITIONS.find(tr => tr.key === transition)?.name ?? transition) : null

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    try {
      await onSave({
        name: name.trim() || t('mix.newName', 'New mix'),
        playlistId, layoutId, audioId, audioPlaylistId, stopScreenId, transition,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open && !saving) onClose() }}>
      <DialogContent className="max-w-5xl p-0 gap-0 overflow-hidden flex flex-col max-h-[92vh]">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
          <DialogTitle>{t('mix.newTitle', 'New Mix')}</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-4 border-b border-border shrink-0">
          <TextInput
            label={t('mix.name', 'Mix name')}
            value={name}
            onChange={setName}
            maxLength={150}
            lang="any"
            placeholder={t('mix.newName', 'New mix')}
            autoFocus
          />
        </div>

        <div className="flex-1 min-h-0 flex">
          <NavRail>
            <NavItem
              active={activeNav === 'content'}
              icon={Clapperboard}
              label={t('mix.mainContent', 'Main content')}
              dot={contentValue != null}
              onClick={() => setActiveNav('content')}
            />
            <NavItem
              active={activeNav === 'audio'}
              icon={Music}
              label={t('companions.audio', 'Audio')}
              dot={audioValue != null}
              onClick={() => setActiveNav('audio')}
            />
            <NavItem
              active={activeNav === 'stopScreen'}
              icon={Hand}
              label={t('companions.stopScreen', 'Stop screen')}
              dot={stopScreenId != null}
              onClick={() => setActiveNav('stopScreen')}
            />
            <NavItem
              active={activeNav === 'transition'}
              icon={Sparkles}
              label={t('companions.transition', 'Transition')}
              dot={transition != null}
              onClick={() => setActiveNav('transition')}
            />
          </NavRail>

          <div className="flex-1 min-w-0 flex flex-col min-h-0">
            {/* Every panel stays mounted (hidden via CSS), matching
                PlaylistDialog/LayoutDialog, so switching tabs never loses
                a pick or an embedded picker's own state (search/filter). */}
            <div className={`flex-1 min-h-0 flex flex-col ${activeNav === 'content' ? '' : 'hidden'}`}>
              <PlaylistContentPicker
                open
                mode="embedded"
                customGroups={contentGroups}
                customSelected={contentValue}
                onCustomSelect={setContentValue}
                onClose={() => {}}
                onConfirm={() => {}}
              />
            </div>
            <div className={`flex-1 min-h-0 flex flex-col ${activeNav === 'audio' ? '' : 'hidden'}`}>
              <PlaylistContentPicker
                open
                mode="embedded"
                customGroups={audioGroups}
                customSelected={audioValue}
                onCustomSelect={setAudioValue}
                onClose={() => {}}
                onConfirm={() => {}}
              />
            </div>
            <div className={`flex-1 min-h-0 flex flex-col ${activeNav === 'stopScreen' ? '' : 'hidden'}`}>
              <PlaylistContentPicker
                open
                mode="embedded"
                customGroups={stopScreenGroups}
                customSelected={stopScreenValue}
                onCustomSelect={setStopScreenValue}
                onClose={() => {}}
                onConfirm={() => {}}
              />
            </div>
            {/* Transition isn't browsable content — same "current pick +
                visual preview picker" pattern CompanionSection uses. */}
            <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 ${activeNav === 'transition' ? '' : 'hidden'}`}>
              <button
                type="button"
                onClick={() => setTransitionPickerOpen(true)}
                className="w-full flex items-center gap-3 rounded-lg border border-border p-3 text-start hover:border-primary/40 transition-colors"
              >
                <div className="w-20 h-14 rounded-md overflow-hidden bg-muted flex items-center justify-center shrink-0">
                  {transition
                    ? <TransitionPreview anim={`tr-${transition}`} className="w-full h-full" logoClass="h-4" />
                    : <Sparkles className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm truncate ${transitionLabel ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {transitionLabel ?? t('companions.none', 'None')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {transitionLabel ? t('companions.publishesTogether', 'Publishes together') : t('companions.tapToChoose', 'Tap to choose')}
                  </p>
                </div>
                {transition && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={e => { e.stopPropagation(); setTransition(null) }}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setTransition(null) } }}
                    aria-label={t('companions.clear', 'Remove')}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" disabled={saving} onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button disabled={saving} onClick={handleSave} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {t('mix.createButton', 'Create Mix')}
          </Button>
        </DialogFooter>
      </DialogContent>

      <TransitionPickerDialog
        open={transitionPickerOpen}
        selected={transition}
        onSelect={setTransition}
        onClose={() => setTransitionPickerOpen(false)}
      />
    </Dialog>
  )
}
